import React, { useState, useMemo } from 'react';
import log from '../../assets/styles/AdjustmentRecords.module.scss';
import CloseBtn from '../closeBtn';
import AddStockEntryForm from './AddStockEntryForm';
import EditFormStockProduct from './EditFormStockProduct';

const getLogTimestamp = (item, useTimestampFirst = false) => {
  if (!item) return 0;

  const parseAnyDate = (val) => {
    if (!val) return null;
    
    // 1. Instância do objeto Date
    if (val instanceof Date) {
      return val.getTime();
    }
    
    // 2. Timestamp do Firestore (objeto com seconds)
    if (typeof val === 'object' && val.seconds !== undefined) {
      return val.seconds * 1000 + (val.nanoseconds ? val.nanoseconds / 1000000 : 0);
    }
    
    // 3. Número (timestamp Unix de milissegundos)
    if (typeof val === 'number') {
      return val;
    }
    
    // 4. String formatada
    if (typeof val === 'string') {
      let str = val.trim();
      if (!str) return null;
      
      // Se for formato ISO string válido
      if (str.includes('T') && !isNaN(Date.parse(str))) {
        return new Date(str).getTime();
      }
      
      let hour = 0;
      let minute = 0;
      let second = 0;
      
      // Expressão regular para capturar e remover a hora (ex: "28/05/2026 - 15:30" ou "28/05/2026 15:30:00")
      const timeRegex = /(?:\s+|-\s+)(\d{2}):(\d{2})(?::(\d{2}))?/;
      const timeMatch = str.match(timeRegex);
      if (timeMatch) {
        hour = parseInt(timeMatch[1], 10) || 0;
        minute = parseInt(timeMatch[2], 10) || 0;
        second = parseInt(timeMatch[3], 10) || 0;
        str = str.replace(timeMatch[0], '').trim();
      }
      
      // Separadores comuns de data (/ ou -)
      const separators = ['/', '-'];
      for (const sep of separators) {
        if (str.includes(sep)) {
          const parts = str.split(sep);
          if (parts.length === 3) {
            // Se o ano estiver no início (YYYY-MM-DD)
            if (parts[0].length === 4) {
              const year = parseInt(parts[0], 10);
              const month = parseInt(parts[1], 10) - 1;
              const day = parseInt(parts[2], 10);
              const d = new Date(year, month, day, hour, minute, second);
              if (!isNaN(d.getTime())) return d.getTime();
            } else {
              // Se o dia estiver no início (DD-MM-YYYY ou DD/MM/YYYY)
              const day = parseInt(parts[0], 10);
              const month = parseInt(parts[1], 10) - 1;
              const year = parseInt(parts[2], 10);
              const d = new Date(year, month, day, hour, minute, second);
              if (!isNaN(d.getTime())) return d.getTime();
            }
          }
        }
      }
      
      // Fallback para parse padrão de data
      const fallback = Date.parse(val);
      if (!isNaN(fallback)) return fallback;
    }
    
    return null;
  };

  const timeFromTimestamp = parseAnyDate(item.timestamp);
  const timeFromDate = parseAnyDate(item.date);

  if (useTimestampFirst) {
    if (timeFromTimestamp !== null) return timeFromTimestamp;
    if (timeFromDate !== null) return timeFromDate;
  } else {
    if (timeFromDate !== null) return timeFromDate;
    if (timeFromTimestamp !== null) return timeFromTimestamp;
  }

  return 0;
};

const AdjustmentRecords = ({
  eventLogData,
  setShowAdjustmentRecords,
  title,
  onRefreshLogs,
}) => {
  const [filter, setFilter] = useState('Todas as Movimentações');
  const [currentPage, setCurrentPage] = useState(1);
  const [editingStockLog, setEditingStockLog] = useState(null);
  const [editingDiscardLog, setEditingDiscardLog] = useState(null);
  const itemsPerPage = 100;

  const filteredData = useMemo(() => {
    if (!eventLogData) return [];
    
    // Ordena os registros de forma consistente:
    // 1. Pelo dia/hora real do evento (campo 'date')
    // 2. Se as datas forem idênticas (ou se uma delas não tiver 'date'), usa o 'timestamp' como critério de desempate.
    let data = [...eventLogData].sort((a, b) => {
      // Prioriza o timestamp exato do sistema (hora em que a ação ocorreu) para garantir a ordem cronológica inversa (mais recente no topo)
      const tsA = getLogTimestamp(a, true);
      const tsB = getLogTimestamp(b, true);
      
      if (tsA !== tsB && tsA > 0 && tsB > 0) {
        return tsB - tsA; // Decrescente (mais recente primeiro)
      }
      
      const dateA = getLogTimestamp(a, false);
      const dateB = getLogTimestamp(b, false);
      return dateB - dateA; // Decrescente (mais recente primeiro)
    });

    if (filter === 'Entrada de MP') {
      data = data.filter(item => {
        const valRaw = item.inputProduct ?? item.entrada ?? 0;
        const val = Math.abs(Number(String(valRaw).replace(',', '.')));
        return val > 0 && !isNaN(val);
      });
      data = data.slice(0, 100);
    } else if (filter === 'Saída de MP') {
      data = data.filter(item => {
        const valRaw = item.outputProduct ?? item.saida ?? 0;
        const val = Math.abs(Number(String(valRaw).replace(',', '.')));
        return val > 0 && !isNaN(val);
      });
      data = data.slice(0, 100);
    } else if (filter === 'Descarte de MP') {
      data = data.filter(item => item.category === 'Editado' || item.category === 'Descarte' || item.adjustmentExpenseNote || item.noteReasonsEditingProduct);
      data = data.slice(0, 100);
    } else if (filter === 'Auditoria de MP') {
      data = data.filter(item => item.category === 'Auditoria');
      data = data.slice(0, 100);
    }
    
    return data;
  }, [eventLogData, filter]);

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  
  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredData.slice(start, start + itemsPerPage);
  }, [filteredData, currentPage]);

  const handleFilterChange = (e) => {
    setFilter(e.target.value);
    setCurrentPage(1);
  };

  const handleCategoryClick = (item, canEdit, isDiscard = false) => {
    if (!canEdit) {
      alert(isDiscard 
        ? "Este descarte foi realizado há mais de 2 horas e não pode mais ser editado."
        : "Esta entrada de estoque foi realizada há mais de 2 horas e não pode mais ser editada ou excluída."
      );
      return;
    }

    if (isDiscard) {
      setEditingDiscardLog({
        ...item,
        stockId: item.stockId || item.id,
        product: item.product || title || '',
        productName: item.productName || item.product || title || '',
      });
    } else {
      setEditingStockLog({
        ...item,
        product: item.product || title || '',
        productName: item.productName || item.product || title || '',
      });
    }
  };

  return (
    <div className={log.containderAdjustmentRecords}>
      <div className={log.containerIcon}>
        <a
          href="https://docs.google.com/document/d/1JO_71SmMvI_lkzAerER1YuuM_F-0Sdp6-dJrdy7E1oQ/edit?tab=t.3z2eh6xgiwvv"
          target="_blank"
          rel="noopener noreferrer"
          title="Abrir documentação"
        >
          <span>?</span>
        </a>
      </div>
      <CloseBtn setClose={setShowAdjustmentRecords} />
      <h1>Lista de Ocorrencias do {title}</h1>
      
      <div style={{ display: 'flex', justifyContent: 'center', margin: '15px 0' }}>
        <select 
          value={filter} 
          onChange={handleFilterChange}
          style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #ccc', fontSize: '1rem', outline: 'none' }}
        >
          <option value="Todas as Movimentações">Todas as Movimentações</option>
          <option value="Entrada de MP">Entrada de MP</option>
          <option value="Saída de MP">Saída de MP</option>
          <option value="Descarte de MP">Descarte de MP</option>
          <option value="Auditoria de MP">Auditoria de MP</option>
        </select>
      </div>

      <div className={log.containderAdjustmentRecordsTable}>
        <table>
          <thead>
            <tr>
              <th title="Data em que o registro foi realizado.">Data</th>
              <th title="Quantidade de produto que entrou no estoque nesta operação.">
                Entrada do produto
              </th>
              <th title="Quantidade de produto que saiu do estoque (venda, perda ou ajuste).">
                Saida
              </th>
              <th title="Número do pedido vinculado à saída, se houver.">
                Pedido
              </th>
              <th title="O tipo de movimentação (Entrada, Saída, Ajuste, Descarte, etc.).">
                Categoria
              </th>
              <th title="O volume que existia no estoque antes desta movimentação.">
                Vol Anterior
              </th>
              <th title="O valor (R$) total investido no estoque deste produto antes da movimentação.">
                Investimento anterior
              </th>
              <th title="O volume resultante no estoque após a movimentação.">
                Volume Atual
              </th>
              <th title="O valor (R$) total investido no estoque após a entrada ou saída.">
                Investimento Atual
              </th>
              <th title="O Custo Médio Ponderado atual (Investimento Atual / Volume Atual).">
                Custo Médio Unit.
              </th>
              <th title="Notas detalhadas sobre o motivo da edição ou a quantidade de embalagens envolvidas.">
                Anotações ou Embalagens
              </th>
            </tr>
          </thead>
          <tbody>
            {paginatedData &&
              paginatedData.length > 0 &&
              paginatedData.map((item, index) => {
                const safeNum = (val) => {
                  const num = Number(val);
                  return isNaN(num) ? 0 : num;
                };
                const vol = safeNum(item.ContentsInStock);
                const inv = safeNum(item.totalResourceInvested);
                const avgCost = vol > 0 ? (inv / vol).toFixed(2) : '0.00';
                const inputVal = safeNum(item.inputProduct ?? item.entrada ?? 0);
                const outputVal = safeNum(item.outputProduct ?? item.saida ?? 0);
                const isStockEntry = inputVal > 0;
                const isDiscard = item.category === 'Descarte' || (outputVal > 0 && item.category !== 'Auditoria' && String(item.category || '').toLowerCase().includes('descarte'));
                const logTime = getLogTimestamp(item, true);
                const canEdit = (isStockEntry || isDiscard) && (Date.now() - logTime) <= (2 * 60 * 60 * 1000);

                return (
                  <tr key={index}>
                    <td>{item.date}</td>
                    <td>
                      {inputVal} {item.unit}
                    </td>
                    <td>{item.outputProduct ?? item.saida ?? 0}</td>
                    <td style={{ fontWeight: item?.orderNumber ? 'bold' : 'normal' }}>
                      {item?.orderNumber || '-'}
                    </td>
                    <td>
                      {isStockEntry || isDiscard ? (
                        <span
                          onClick={() => handleCategoryClick(item, canEdit, isDiscard)}
                          style={{
                            color: canEdit ? '#007bff' : '#6c757d',
                            cursor: 'pointer',
                            textDecoration: 'underline',
                            fontWeight: 'bold',
                          }}
                          title={
                            canEdit
                              ? (isDiscard ? "Clique para editar este descarte" : "Clique para editar ou excluir esta entrada de estoque")
                              : (isDiscard ? "Descarte realizado há mais de 2 horas (não editável)" : "Entrada realizada há mais de 2 horas (não editável)")
                          }
                        >
                          {item.category}
                        </span>
                      ) : (
                        item.category
                      )}
                    </td>
                    <td>
                      {safeNum(item.previousVolume).toFixed(2)} {item.unit}
                    </td>
                    <td>R${safeNum(item.previousCost).toFixed(2)}</td>
                    <td>
                      {' '}
                      {safeNum(item.ContentsInStock).toFixed(2)}
                      {item.unit}
                    </td>
                    <td>R$ {safeNum(item.totalResourceInvested).toFixed(2)}</td>
                    <td style={{ fontWeight: 'bold', color: '#14213D' }}>R$ {avgCost} / {item.unit}</td>
                    <td
                      title={
                        item.noteReasonsEditingProduct
                          ? item.noteReasonsEditingProduct
                          : item.adjustmentExpenseNote
                      }
                    >
                      {item.category === 'Editado' || item.category === 'Descarte' || item.category === 'Auditoria' || item.adjustmentExpenseNote
                        ? item.noteReasonsEditingProduct ||
                          item.adjustmentExpenseNote ||
                          'sem notas'
                        : item.package}
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>

      {filter === 'Todas as Movimentações' && totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: '20px', alignItems: 'center', margin: '20px 0' }}>
          <button 
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            style={{ padding: '8px 16px', borderRadius: '6px', cursor: currentPage === 1 ? 'not-allowed' : 'pointer' }}
          >
            Anterior
          </button>
          <span>Página {currentPage} de {totalPages}</span>
          <button 
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
            style={{ padding: '8px 16px', borderRadius: '6px', cursor: currentPage === totalPages ? 'not-allowed' : 'pointer' }}
          >
            Próximo
          </button>
        </div>
      )}

      {editingStockLog && (
        <AddStockEntryForm
          setShowPopup={() => setEditingStockLog(null)}
          editingLog={editingStockLog}
          onLogDeleted={() => {
            setEditingStockLog(null);
            if (onRefreshLogs) onRefreshLogs();
          }}
          onLogUpdated={() => {
            setEditingStockLog(null);
            if (onRefreshLogs) onRefreshLogs();
          }}
        />
      )}

      {editingDiscardLog && (
        <EditFormStockProduct
          obj={{
            id: editingDiscardLog.stockId || editingDiscardLog.id,
            product: editingDiscardLog.product || title || '',
            unitOfMeasurement: editingDiscardLog.unit || '',
            totalVolume: editingDiscardLog.ContentsInStock || 0,
            totalCost: editingDiscardLog.totalResourceInvested || 0,
          }}
          editingLog={editingDiscardLog}
          setShowEditForm={() => setEditingDiscardLog(null)}
          fetchStock={() => {
            setEditingDiscardLog(null);
            if (onRefreshLogs) onRefreshLogs();
          }}
        />
      )}
    </div>
  );
};
export default AdjustmentRecords;
